import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Video, Plus, Calendar as CalendarIcon, Clock, Users,
  Check, X, Play, Square, FileText, Search, Mic, Bot, FolderOpen, AlertTriangle,
  History, CircleDot, Globe, Mail, Languages
} from "lucide-react";
import { format, parseISO, isToday, isTomorrow, isSameDay, isPast } from "date-fns";
import AINotetaker from "@/components/AINotetaker";
import MeetAIPanel from "@/components/MeetAIPanel";
import { LANG_NAMES as SUPPORTED_LANGUAGES_FROM_CONFIG } from "@/config/languages";
import NotificationBell from "@/components/NotificationBell";
import TolokyControlPanel from "@/components/comms/toloky/TolokyControlPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Meetings() {
  const [meetings, setMeetings] = useState([]);
  const [pastMeetings, setPastMeetings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [selectedMeetingForAI, setSelectedMeetingForAI] = useState(null);
  const [isMeetAIOpen, setIsMeetAIOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [activeTab, setActiveTab] = useState("invites"); // invites, upcoming, or attended
  const [agendaItems, setAgendaItems] = useState([""]); // Agenda items array
  const [defaultLanguage, setDefaultLanguage] = useState("en"); // Meeting default language
  const [invites, setInvites] = useState([]); // Pending invitations
  
  // Supported languages for MeetAI interpretation (shared 33-language list)
  const SUPPORTED_LANGUAGES = { ...SUPPORTED_LANGUAGES_FROM_CONFIG };
  
  const [newMeeting, setNewMeeting] = useState({
    title: "",
    description: "",
    scheduled_at: "",
    scheduled_time: "10:00",
    duration_minutes: 60,
    review_period_days: 3
  });

  const addAgendaItem = () => {
    setAgendaItems([...agendaItems, ""]);
  };

  const updateAgendaItem = (index, value) => {
    const updated = [...agendaItems];
    updated[index] = value;
    setAgendaItems(updated);
  };

  const removeAgendaItem = (index) => {
    if (agendaItems.length > 1) {
      const updated = agendaItems.filter((_, i) => i !== index);
      setAgendaItems(updated);
    }
  };

  const openMeetAI = (meeting) => {
    setSelectedMeetingForAI(meeting);
    setIsMeetAIOpen(true);
  };

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchMeetings();
    fetchInvites();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setCurrentUserId(res.data.user_id);
    } catch (err) {
      console.error("Failed to fetch user");
    }
  };

  const fetchMeetings = async () => {
    try {
      const res = await axios.get(`${API}/comms/meetings?include_past=true`, {
        headers: getHeaders(),
        withCredentials: true
      });
      // Separate upcoming/active and past meetings
      const all = res.data;
      const upcoming = all.filter(m => m.status === "scheduled" || m.status === "in_progress");
      const past = all.filter(m => m.status === "completed").sort((a, b) => 
        new Date(b.scheduled_at) - new Date(a.scheduled_at)
      );
      setMeetings(upcoming);
      setPastMeetings(past);
    } catch (err) {
      toast.error("Failed to load meetings");
    }
  };

  const fetchInvites = async () => {
    try {
      const res = await axios.get(`${API}/comms/meetings/invites`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setInvites(res.data);
      // Auto-switch to invites tab if there are pending invites
      if (res.data.length > 0 && activeTab === "upcoming") {
        setActiveTab("invites");
      }
    } catch (err) {
      console.error("Failed to load invites");
    }
  };

  const createMeeting = async () => {
    if (!newMeeting.title || !newMeeting.scheduled_at) {
      toast.error("Title and date are required");
      return;
    }
    
    const scheduled_at = `${newMeeting.scheduled_at}T${newMeeting.scheduled_time}:00Z`;
    
    // Filter out empty agenda items
    const validAgendaItems = agendaItems.filter(item => item.trim() !== "");
    
    try {
      const res = await axios.post(`${API}/comms/meetings`, {
        title: newMeeting.title,
        description: newMeeting.description,
        scheduled_at,
        duration_minutes: newMeeting.duration_minutes,
        participant_ids: selectedParticipants.map(p => p.user_id),
        review_period_days: newMeeting.review_period_days,
        agenda_items: validAgendaItems,
        default_language: defaultLanguage
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      
      // Show conflicts if any
      if (res.data.conflicts_detected?.length > 0) {
        toast.warning(`Meeting created. ${res.data.conflicts_detected.length} participant(s) have schedule conflicts.`);
      } else {
        toast.success("Meeting scheduled with MeetAI folder created");
      }
      
      setIsCreateOpen(false);
      setNewMeeting({ title: "", description: "", scheduled_at: "", scheduled_time: "10:00", duration_minutes: 60, review_period_days: 3 });
      setSelectedParticipants([]);
      setAgendaItems([""]);
      setDefaultLanguage("en");
      fetchMeetings();
    } catch (err) {
      toast.error("Failed to create meeting");
    }
  };

  const [respondDialogOpen, setRespondDialogOpen] = useState(false);
  const [selectedMeetingToRespond, setSelectedMeetingToRespond] = useState(null);
  const [selectedLanguageForResponse, setSelectedLanguageForResponse] = useState("en");

  const openRespondDialog = (meeting) => {
    setSelectedMeetingToRespond(meeting);
    setSelectedLanguageForResponse(meeting.default_language || "en");
    setRespondDialogOpen(true);
  };

  const respondToMeeting = async (meetingId, response, preferredLanguage = "en") => {
    try {
      await axios.post(`${API}/comms/meetings/${meetingId}/respond`, 
        { response, preferred_language: preferredLanguage },
        { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true }
      );
      toast.success(`Meeting ${response}ed` + (response === "confirm" ? ` with ${SUPPORTED_LANGUAGES[preferredLanguage]} preference` : ""));
      setRespondDialogOpen(false);
      setSelectedMeetingToRespond(null);
      fetchMeetings();
      fetchInvites();
    } catch (err) {
      toast.error("Failed to respond");
    }
  };

  const startMeeting = async (meetingId) => {
    try {
      await axios.post(`${API}/comms/meetings/${meetingId}/start`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("Meeting started");
      fetchMeetings();
    } catch (err) {
      toast.error("Failed to start meeting");
    }
  };

  const endMeeting = async (meetingId) => {
    try {
      await axios.post(`${API}/comms/meetings/${meetingId}/end`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("Meeting ended");
      fetchMeetings();
    } catch (err) {
      toast.error("Failed to end meeting");
    }
  };

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/comms/chat/users/search?q=${encodeURIComponent(query)}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setSearchResults(res.data.filter(u => !selectedParticipants.find(p => p.user_id === u.user_id)));
    } catch (err) {
      console.error("Search failed");
    }
  };

  const addParticipant = (user) => {
    setSelectedParticipants([...selectedParticipants, user]);
    setSearchResults([]);
    setSearchQuery("");
  };

  const removeParticipant = (userId) => {
    setSelectedParticipants(selectedParticipants.filter(p => p.user_id !== userId));
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getMeetingsForDate = (date) => {
    // Combine all meetings and deduplicate by meeting ID
    const allMeetingsMap = new Map();
    [...meetings, ...pastMeetings, ...invites].forEach(m => {
      if (!allMeetingsMap.has(m.id)) {
        allMeetingsMap.set(m.id, m);
      }
    });
    
    return Array.from(allMeetingsMap.values()).filter(m => {
      const meetingDate = parseISO(m.scheduled_at);
      return isSameDay(meetingDate, date);
    });
  };

  // Get meeting color for calendar based on invite status
  const getMeetingColor = (meeting) => {
    if (meeting.status === "completed") return "#6B7280"; // Dark grey - attended
    if (meeting.invite_status === "declined") return "#EF4444"; // Red - declined
    if (meeting.invite_status === "pending") return "#EF4444"; // Red - pending invite
    if (meeting.invite_status === "accepted" || meeting.invite_status === "creator") return "#22C55E"; // Green - accepted
    return "#8B5CF6"; // Violet - default
  };

  const getDateLabel = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return "Today";
    if (isTomorrow(date)) return "Tomorrow";
    return format(date, "MMM d, yyyy");
  };

  // Get minutes status indicator color
  const getMinutesStatusIndicator = (meeting) => {
    // Red: Before/during meeting (no minutes yet)
    if (meeting.status === "scheduled" || meeting.status === "in_progress") {
      return (
        <span className="flex items-center gap-1" title="Minutes: Not yet generated">
          <CircleDot className="w-3 h-3 text-red-500" />
          <span className="text-xs text-red-600">No Minutes</span>
        </span>
      );
    }
    
    // Get minutes_status from meeting
    const minutesStatus = meeting.minutes_status || "pending";
    
    // Red: Meeting completed but no draft minutes yet
    if (minutesStatus === "pending") {
      return (
        <span className="flex items-center gap-1" title="Minutes: Awaiting generation">
          <CircleDot className="w-3 h-3 text-red-500" />
          <span className="text-xs text-red-600">No Minutes</span>
        </span>
      );
    }
    
    // Amber/Yellow: Draft generated, in review period
    if (minutesStatus === "in_review") {
      return (
        <span className="flex items-center gap-1" title="Minutes: Under review - collecting feedback">
          <CircleDot className="w-3 h-3 text-amber-500" />
          <span className="text-xs text-amber-600">Minutes in Progress</span>
        </span>
      );
    }
    
    // Green: Minutes finalized
    if (minutesStatus === "finalized") {
      return (
        <span className="flex items-center gap-1" title="Minutes: Completed">
          <CircleDot className="w-3 h-3 text-green-500" />
          <span className="text-xs text-green-600">Minutes Completed</span>
        </span>
      );
    }
    
    return null;
  };

  const getStatusBadge = (meeting) => {
    const isConfirmed = meeting.confirmed_participants?.includes(currentUserId);
    const isDeclined = meeting.declined_participants?.includes(currentUserId);
    
    if (meeting.status === "in_progress") {
      return <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">In Progress</span>;
    }
    if (meeting.status === "completed") {
      return <span className="bg-slate-100 text-app text-xs px-2 py-1 rounded-full">Completed</span>;
    }
    if (isDeclined) {
      return <span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full">Declined</span>;
    }
    if (isConfirmed) {
      return <span className="bg-violet-100 text-violet-600 text-xs px-2 py-1 rounded-full">Confirmed</span>;
    }
    return <span className="bg-amber-100 text-amber-600 text-xs px-2 py-1 rounded-full">Pending</span>;
  };

  const selectedDateMeetings = getMeetingsForDate(selectedDate);
  const upcomingMeetings = meetings;

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <header className="border-b border-app bg-app-card px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-app">
            MEETINGS
          </h1>
          <p className="text-sm text-app-secondary">Video conferences & scheduling powered by MeetAI</p>
        </div>
        
        <div className="flex items-center gap-3">
          <NotificationBell />
          
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-violet-500 hover:bg-violet-600 rounded-lg" data-testid="schedule-meeting-btn">
                <Plus className="w-4 h-4 mr-2" />
                Schedule Meeting
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-app-card max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] text-lg">Schedule New Meeting</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs">Meeting Title</Label>
                <Input
                  value={newMeeting.title}
                  onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })}
                  placeholder="Team Standup"
                  className="mt-0.5 h-8 text-sm"
                  data-testid="meeting-title-input"
                />
              </div>
              
              <div>
                <Label className="text-xs">Description (optional)</Label>
                <Textarea
                  value={newMeeting.description}
                  onChange={(e) => setNewMeeting({ ...newMeeting, description: e.target.value })}
                  placeholder="Meeting agenda..."
                  className="mt-0.5 h-14 text-sm resize-none"
                  data-testid="meeting-description-input"
                />
              </div>
              
              <div className="grid grid-cols-4 gap-2">
                <div className="col-span-1">
                  <Label className="text-xs">Date</Label>
                  <Input
                    type="date"
                    value={newMeeting.scheduled_at}
                    onChange={(e) => setNewMeeting({ ...newMeeting, scheduled_at: e.target.value })}
                    className="mt-0.5 h-8 text-sm"
                    data-testid="meeting-date-input"
                  />
                </div>
                <div className="col-span-1">
                  <Label className="text-xs">Time</Label>
                  <Input
                    type="time"
                    value={newMeeting.scheduled_time}
                    onChange={(e) => setNewMeeting({ ...newMeeting, scheduled_time: e.target.value })}
                    className="mt-0.5 h-8 text-sm"
                    data-testid="meeting-time-input"
                  />
                </div>
                <div className="col-span-1">
                  <Label className="text-xs">Duration</Label>
                  <Select 
                    value={String(newMeeting.duration_minutes)} 
                    onValueChange={(v) => setNewMeeting({ ...newMeeting, duration_minutes: parseInt(v) })}
                  >
                    <SelectTrigger className="mt-0.5 h-8 text-sm" data-testid="meeting-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 min</SelectItem>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="45">45 min</SelectItem>
                      <SelectItem value="60">1 hr</SelectItem>
                      <SelectItem value="90">1.5 hr</SelectItem>
                      <SelectItem value="120">2 hr</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-1">
                  <Label className="text-xs">Review</Label>
                  <Select 
                    value={String(newMeeting.review_period_days)} 
                    onValueChange={(v) => setNewMeeting({ ...newMeeting, review_period_days: parseInt(v) })}
                  >
                    <SelectTrigger className="mt-0.5 h-8 text-sm" data-testid="review-period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 day</SelectItem>
                      <SelectItem value="2">2 days</SelectItem>
                      <SelectItem value="3">3 days</SelectItem>
                      <SelectItem value="5">5 days</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* MeetAI Interpretation - Default Language */}
              <div>
                <Label className="text-xs">Default Language (MeetAI Interpretation)</Label>
                <Select 
                  value={defaultLanguage} 
                  onValueChange={(v) => setDefaultLanguage(v)}
                >
                  <SelectTrigger className="mt-0.5 h-8 text-sm" data-testid="default-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => (
                      <SelectItem key={code} value={code}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-xs">Add Participants</Label>
                <div className="relative mt-0.5">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-app-secondary" />
                  <Input
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      searchUsers(e.target.value);
                    }}
                    className="pl-7 h-8 text-sm"
                    data-testid="participant-search-input"
                  />
                </div>
                {searchResults.length > 0 && (
                  <div className="mt-1 border rounded-lg max-h-24 overflow-auto">
                    {searchResults.map((user) => (
                      <button
                        key={user.user_id}
                        onClick={() => addParticipant(user)}
                        className="w-full flex items-center gap-2 p-1.5 hover:bg-app text-left text-xs"
                      >
                        <Avatar className="w-5 h-5">
                          <AvatarImage src={user.picture} />
                          <AvatarFallback className="text-xs bg-violet-100 text-violet-600">
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        {user.name}
                      </button>
                    ))}
                  </div>
                )}
                
                {selectedParticipants.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedParticipants.map((p) => (
                      <span 
                        key={p.user_id}
                        className="inline-flex items-center gap-1 bg-violet-100 text-violet-700 text-xs px-1.5 py-0.5 rounded-full"
                      >
                        {p.name}
                        <button onClick={() => removeParticipant(p.user_id)} className="hover:text-violet-900">
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Agenda Items Section */}
              <div className="border-t pt-2">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Agenda - Points of Discussion (Optional)</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={addAgendaItem}
                    className="text-violet-600 hover:text-violet-700 h-6 px-1.5 text-xs"
                  >
                    <Plus className="w-3 h-3 mr-0.5" />
                    Add
                  </Button>
                </div>
                <div className="space-y-1 max-h-24 overflow-auto">
                  {agendaItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-1">
                      <span className="text-xs text-app-secondary w-4">{index + 1}.</span>
                      <Input
                        value={item}
                        onChange={(e) => updateAgendaItem(index, e.target.value)}
                        placeholder={`Point ${index + 1}...`}
                        className="flex-1 h-7 text-xs"
                        data-testid={`agenda-item-${index}`}
                      />
                      {agendaItems.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAgendaItem(index)}
                          className="text-red-500 hover:text-red-600 h-6 w-6 p-0"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <Button onClick={createMeeting} className="w-full h-9 bg-violet-500 hover:bg-violet-600 text-sm" data-testid="create-meeting-btn">
                Schedule Meeting
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Calendar Sidebar */}
        <div className="w-80 border-r border-app bg-app-card p-4 flex flex-col">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => date && setSelectedDate(date)}
            className="rounded-lg border-0"
            modifiers={{
              hasMeeting: (date) => getMeetingsForDate(date).length > 0
            }}
            modifiersStyles={{
              hasMeeting: { fontWeight: "bold", color: "#8B5CF6" }
            }}
            data-testid="meeting-calendar"
          />
          
          <div className="mt-4 pt-4 border-t border-app">
            <h3 className="font-medium text-app mb-2">
              {format(selectedDate, "MMMM d, yyyy")}
            </h3>
            {selectedDateMeetings.length > 0 ? (
              <div className="space-y-2">
                {selectedDateMeetings.map((m) => {
                  // Determine color based on status
                  const isInvite = invites.some(inv => inv.id === m.id);
                  const isDeclined = m.declined_participants?.includes(currentUserId);
                  const isAccepted = m.confirmed_participants?.includes(currentUserId) || m.creator_id === currentUserId;
                  
                  let bgClass = "bg-violet-50 border-l-violet-500";
                  let statusLabel = "";
                  
                  if (m.status === "completed") {
                    bgClass = "bg-slate-100 border-l-slate-500"; // Dark grey - attended
                    statusLabel = "Attended";
                  } else if (isDeclined) {
                    bgClass = "bg-red-50 border-l-red-500"; // Red - declined
                    statusLabel = "Declined";
                  } else if (isInvite) {
                    bgClass = "bg-red-50 border-l-red-500 animate-pulse-subtle"; // Red flashing - pending invite
                    statusLabel = "Pending";
                  } else if (isAccepted) {
                    bgClass = "bg-green-50 border-l-green-500"; // Green - accepted
                    statusLabel = "Accepted";
                  }
                  
                  return (
                    <div 
                      key={m.id} 
                      className={`p-2 rounded-lg text-sm cursor-pointer hover:opacity-80 border-l-4 ${bgClass}`}
                      onClick={() => openMeetAI(m)}
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-app truncate">{m.title}</p>
                        {statusLabel && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            statusLabel === "Attended" ? "bg-slate-200 text-app" :
                            statusLabel === "Declined" ? "bg-red-200 text-red-700" :
                            statusLabel === "Pending" ? "bg-red-200 text-red-700" :
                            "bg-green-200 text-green-700"
                          }`}>
                            {statusLabel}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-app-secondary">
                          {format(parseISO(m.scheduled_at), "HH:mm")} - {m.duration_minutes} min
                        </p>
                        {m.status === "completed" && getMinutesStatusIndicator(m)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-app-secondary">No meetings scheduled</p>
            )}
          </div>
          
          {/* Meetings Attended Section */}
          <div className="mt-4 pt-4 border-t border-app flex-1 overflow-hidden flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-app" />
              <h3 className="font-medium text-app">Meetings Attended</h3>
            </div>
            <ScrollArea className="flex-1">
              {pastMeetings.length > 0 ? (
                <div className="space-y-2 pr-2">
                  {pastMeetings.slice(0, 10).map((m) => (
                    <div
                      key={m.id}
                      className="p-2 bg-app hover:bg-slate-100 rounded-lg cursor-pointer transition-colors"
                      onClick={() => openMeetAI(m)}
                      data-testid={`past-meeting-${m.id}`}
                    >
                      <p className="font-medium text-sm text-app truncate">{m.title}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-app-secondary">
                          {format(parseISO(m.scheduled_at), "d MMM yyyy")}
                        </p>
                        {getMinutesStatusIndicator(m)}
                      </div>
                    </div>
                  ))}
                  {pastMeetings.length > 10 && (
                    <p className="text-xs text-app-secondary text-center py-2">
                      +{pastMeetings.length - 10} more meetings
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-app-secondary">No past meetings</p>
              )}
            </ScrollArea>
          </div>
        </div>

        {/* Main Content Area with Tabs */}
        <div className="flex-1 flex flex-col overflow-hidden bg-app">
          {/* Tab Buttons */}
          <div className="flex border-b border-app bg-app-card px-6">
            <button
              onClick={() => setActiveTab("invites")}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors relative ${
                activeTab === "invites"
                  ? "border-red-500 text-red-600"
                  : "border-transparent text-app-secondary hover:text-app"
              }`}
              data-testid="invites-tab"
            >
              {invites.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                  {invites.length}
                </span>
              )}
              Invites ({invites.length})
            </button>
            <button
              onClick={() => setActiveTab("upcoming")}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "upcoming"
                  ? "border-violet-500 text-violet-600"
                  : "border-transparent text-app-secondary hover:text-app"
              }`}
              data-testid="upcoming-tab"
            >
              Upcoming ({upcomingMeetings.length})
            </button>
            <button
              onClick={() => setActiveTab("attended")}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "attended"
                  ? "border-violet-500 text-violet-600"
                  : "border-transparent text-app-secondary hover:text-app"
              }`}
              data-testid="attended-tab"
            >
              Attended ({pastMeetings.length})
            </button>
          </div>
          
          {/* Tab Content */}
          <div className="flex-1 p-6 overflow-auto">
            {activeTab === "invites" ? (
              /* Invites Tab Content */
              <>
                <h2 className="font-['Barlow_Condensed'] text-xl font-semibold text-app mb-4">
                  MEETING INVITATIONS ({invites.length})
                </h2>
                
                {invites.length === 0 ? (
                  <div className="text-center py-12">
                    <Mail className="w-16 h-16 text-app-secondary mx-auto mb-4" />
                    <p className="text-app-secondary">No pending invitations</p>
                    <p className="text-app-secondary text-sm">Meeting invites will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {invites.map((meeting) => (
                      <div 
                        key={meeting.id}
                        className="bg-app-card border-2 border-red-300 rounded-lg p-4 animate-pulse-subtle relative overflow-hidden"
                        data-testid={`invite-${meeting.id}`}
                      >
                        {/* Flashing red indicator */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-red-500 animate-pulse"></div>
                        
                        <div className="flex items-start justify-between ml-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="w-3 h-3 bg-red-500 rounded-full animate-ping"></span>
                              <h3 className="font-semibold text-app">{meeting.title}</h3>
                              <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full font-medium">
                                Pending Invite
                              </span>
                            </div>
                            
                            <p className="text-sm text-app mb-2">
                              From: <span className="font-medium">{meeting.creator_name}</span>
                              {meeting.creator_username && (
                                <span className="text-app-secondary ml-1">@{meeting.creator_username}</span>
                              )}
                            </p>
                            
                            {meeting.description && (
                              <p className="text-sm text-app mb-2">{meeting.description}</p>
                            )}
                            
                            <div className="flex items-center gap-4 text-sm text-app-secondary">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="w-4 h-4" />
                                {getDateLabel(meeting.scheduled_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {format(parseISO(meeting.scheduled_at), "HH:mm")} ({meeting.duration_minutes} min)
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {meeting.participant_details?.length || 0} participants
                              </span>
                              {meeting.default_language && meeting.default_language !== "en" && (
                                <span className="flex items-center gap-1 text-violet-600">
                                  <Globe className="w-4 h-4" />
                                  {SUPPORTED_LANGUAGES[meeting.default_language]}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Accept/Decline Actions */}
                          <div className="flex flex-col gap-2 ml-4">
                            <Button
                              size="sm"
                              onClick={() => openRespondDialog(meeting)}
                              className="bg-green-500 hover:bg-green-600 gap-1"
                              data-testid={`accept-invite-${meeting.id}`}
                            >
                              <Check className="w-4 h-4" />
                              Accept
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => respondToMeeting(meeting.id, "decline")}
                              className="border-red-300 text-red-600 hover:bg-red-50 gap-1"
                              data-testid={`decline-invite-${meeting.id}`}
                            >
                              <X className="w-4 h-4" />
                              Decline
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : activeTab === "upcoming" ? (
              <>
                <h2 className="font-['Barlow_Condensed'] text-xl font-semibold text-app mb-4">
                  UPCOMING MEETINGS ({upcomingMeetings.length})
                </h2>
                
                {upcomingMeetings.length === 0 ? (
                  <div className="text-center py-12">
                    <Video className="w-16 h-16 text-app-secondary mx-auto mb-4" />
                    <p className="text-app-secondary">No upcoming meetings</p>
                    <p className="text-app-secondary text-sm">Schedule a meeting to get started</p>
                  </div>
                ) : (
                  <div className="space-y-4">
              {upcomingMeetings.map((meeting) => (
                <div 
                  key={meeting.id}
                  className="bg-app-card border border-app rounded-lg p-4"
                  data-testid={`meeting-${meeting.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-app">{meeting.title}</h3>
                        {getStatusBadge(meeting)}
                        {getMinutesStatusIndicator(meeting)}
                      </div>
                      
                      {meeting.description && (
                        <p className="text-sm text-app mb-2">{meeting.description}</p>
                      )}
                      
                      <div className="flex items-center gap-4 text-sm text-app-secondary">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          {getDateLabel(meeting.scheduled_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {format(parseISO(meeting.scheduled_at), "HH:mm")} ({meeting.duration_minutes} min)
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {meeting.participant_details?.length || 0} participants
                        </span>
                        <span className="flex items-center gap-1 text-violet-600">
                          <FolderOpen className="w-4 h-4" />
                          Folder
                        </span>
                      </div>
                      
                      {/* Participants */}
                      <div className="flex items-center gap-1 mt-3">
                        {meeting.participant_details?.slice(0, 5).map((p) => (
                          <Avatar key={p.user_id} className="w-6 h-6 border-2 border-white">
                            <AvatarImage src={p.picture} />
                            <AvatarFallback className="text-xs bg-violet-100 text-violet-600">
                              {getInitials(p.name)}
                            </AvatarFallback>
                          </Avatar>
                        ))}
                        {meeting.participant_details?.length > 5 && (
                          <span className="text-xs text-app-secondary ml-1">
                            +{meeting.participant_details.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-2 ml-4">
                      {/* MeetAI Button with Folder - always available */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openMeetAI(meeting)}
                        className="gap-1 border-violet-300 text-violet-700 hover:bg-violet-50"
                        data-testid={`meetai-btn-${meeting.id}`}
                      >
                        <FolderOpen className="w-4 h-4" />
                        MeetAI & Folder
                      </Button>

                      {/* Toloky Interpreter */}
                      <TolokyControlPanel
                        meetingId={meeting.id}
                        meetingTitle={meeting.title}
                        compact
                      />
                      
                      {/* AI Notetaker - available for in-progress meetings */}
                      {meeting.status === "in_progress" && (
                        <AINotetaker
                          sourceType="meeting"
                          sourceId={meeting.id}
                          sourceName={meeting.title}
                        />
                      )}
                      
                      {meeting.status === "scheduled" && meeting.creator_id === currentUserId && (
                        <Button
                          size="sm"
                          onClick={() => startMeeting(meeting.id)}
                          className="bg-green-500 hover:bg-green-600"
                          data-testid={`start-meeting-${meeting.id}`}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Start
                        </Button>
                      )}
                      
                      {meeting.status === "in_progress" && meeting.creator_id === currentUserId && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => endMeeting(meeting.id)}
                          className="border-red-200 text-red-500 hover:bg-red-50"
                          data-testid={`end-meeting-${meeting.id}`}
                        >
                          <Square className="w-4 h-4 mr-1" />
                          End
                        </Button>
                      )}
                      
                      {meeting.status === "scheduled" && 
                       meeting.creator_id !== currentUserId && 
                       !meeting.confirmed_participants?.includes(currentUserId) &&
                       !meeting.declined_participants?.includes(currentUserId) && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            onClick={() => openRespondDialog(meeting)}
                            className="bg-green-500 hover:bg-green-600"
                            data-testid={`confirm-meeting-${meeting.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => respondToMeeting(meeting.id, "decline")}
                            className="border-red-200 text-red-500 hover:bg-red-50"
                            data-testid={`decline-meeting-${meeting.id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      {/* Show language preference for confirmed attendees */}
                      {meeting.status === "scheduled" && 
                       meeting.confirmed_participants?.includes(currentUserId) &&
                       meeting.participant_languages?.[currentUserId] && (
                        <span className="text-xs text-app-secondary flex items-center gap-1">
                          <Globe className="w-3 h-3" />
                          {SUPPORTED_LANGUAGES[meeting.participant_languages[currentUserId]] || "English"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
              </>
            ) : (
              /* Attended Tab Content */
              <>
                <h2 className="font-['Barlow_Condensed'] text-xl font-semibold text-app mb-4">
                  MEETINGS ATTENDED ({pastMeetings.length})
                </h2>
                
                {pastMeetings.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="w-16 h-16 text-app-secondary mx-auto mb-4" />
                    <p className="text-app-secondary">No past meetings</p>
                    <p className="text-app-secondary text-sm">Your completed meetings will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pastMeetings.map((meeting) => (
                      <div 
                        key={meeting.id}
                        className="bg-app-card border border-app rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => openMeetAI(meeting)}
                        data-testid={`attended-meeting-${meeting.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-app">{meeting.title}</h3>
                              {getStatusBadge(meeting)}
                              {getMinutesStatusIndicator(meeting)}
                            </div>
                            
                            {meeting.description && (
                              <p className="text-sm text-app mb-2">{meeting.description}</p>
                            )}
                            
                            <div className="flex items-center gap-4 text-sm text-app-secondary">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="w-4 h-4" />
                                {format(parseISO(meeting.scheduled_at), "d MMMM yyyy")}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {format(parseISO(meeting.scheduled_at), "HH:mm")}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {meeting.participant_details?.length || 0} participants
                              </span>
                              {meeting.has_transcription && (
                                <span className="flex items-center gap-1 text-violet-600">
                                  <FileText className="w-4 h-4" />
                                  Transcription
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 border-violet-300 text-violet-700 hover:bg-violet-50 ml-4"
                          >
                            <FolderOpen className="w-4 h-4" />
                            Open Folder
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* MeetAI Panel */}
      <MeetAIPanel
        meeting={selectedMeetingForAI}
        isOpen={isMeetAIOpen}
        onClose={() => {
          setIsMeetAIOpen(false);
          setSelectedMeetingForAI(null);
        }}
        onUpdate={fetchMeetings}
      />
      
      {/* Language Selection Dialog for Meeting Confirmation */}
      <Dialog open={respondDialogOpen} onOpenChange={setRespondDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-violet-500" />
              Confirm Attendance
            </DialogTitle>
          </DialogHeader>
          
          {selectedMeetingToRespond && (
            <div className="space-y-4">
              <div>
                <p className="font-medium text-app">{selectedMeetingToRespond.title}</p>
                <p className="text-sm text-app-secondary">
                  {format(parseISO(selectedMeetingToRespond.scheduled_at), "MMMM d, yyyy 'at' HH:mm")}
                </p>
              </div>
              
              <div className="bg-violet-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-violet-800 mb-2">MeetAI Interpretation</p>
                <p className="text-xs text-violet-600 mb-3">
                  Select your preferred language. MeetAI will provide real-time interpretation during the meeting.
                </p>
                
                <Label className="text-sm">Your Preferred Language</Label>
                <Select 
                  value={selectedLanguageForResponse} 
                  onValueChange={setSelectedLanguageForResponse}
                >
                  <SelectTrigger className="mt-1" data-testid="response-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => (
                      <SelectItem key={code} value={code}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <p className="text-xs text-app-secondary mt-2">
                  Meeting default: {SUPPORTED_LANGUAGES[selectedMeetingToRespond.default_language || "en"]}
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => respondToMeeting(selectedMeetingToRespond.id, "confirm", selectedLanguageForResponse)}
                  className="flex-1 bg-green-500 hover:bg-green-600"
                  data-testid="confirm-with-language-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Confirm Attendance
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setRespondDialogOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
